import { ThemeConfig, AGNT_INC_THEME_NAME, ThemeColors } from '@tenants/types';
import { SiteLanguage } from 'types/locales';
import { AGNT_INC_WP_UPLOADS_URL } from 'constants/domains';


// ---------------------------------------------------------------------------
// Design tokens — sourced from https://anniewang-exp-exp-world-holdings.vercel.app
// CSS variables extracted directly from the page:
//   --navy: #1B2A4E  --navy-deep: #0F1A33  --navy-ink: #0A1428
//   --blue: #1E4794  --blue-soft: #EDF0F6  --font: 'Manrope'
// ---------------------------------------------------------------------------

export const AgntIncThemeColors: ThemeColors = {
  primaryVeryLight: '#EDF0F6',   // --blue-soft
  primaryLight: '#E5E8EE',       // --light-gray
  primary: '#1B2A4E',            // --navy  (body text + primary)
  primaryDark: '#0F1A33',        // --navy-deep (footer bg)
  primaryVeryDark: '#0A1428',    // --navy-ink  (hero bg)
  primaryBackground: '#F7F8FA',  // --off-white
  primaryFontColor: '#1B2A4E',   // --navy
  primaryForSale: '#FFFFFF',
  primarySold: '#1B2A4E',
  primarySoldFocus: '#0F1A33',
  primaryPending: '#1E4794',
  primaryPendingFocus: '#1B2A4E',
  primaryFilters: '#1B2A4E',
  primaryLinkColor: '#1E4794',   // --blue (CTA / links)
  primaryFocusLinkColor: '#1B2A4E',
  primaryInvertedBackground: '#FFFFFF',
  primaryBorderColor: '#E5E8EE', // --light-gray
  primaryFocusColor: '#EDF0F6',  // --blue-soft
  primaryFocusBorderColor: '#1B2A4E',
  primaryChart: '#EDF0F6',
  secondaryBackground: '#FFFFFF',
  secondaryBorderColor: '#E5E8EE',
  secondaryLinkColor: '#3A4257', // --dark-gray
  secondaryHoverColor: '#0F1A33',
  secondaryFocusColor: '#3A4257',
  secondaryFocusBorderColor: '#E5E8EE',
  tertiaryBackground: '#F7F8FA',
  tertiaryBackgroundLight: '#3A4257',
  tertiaryBorderColor: '#E5E8EE',
  tertiaryLinkColor: '#1E4794',
  quaternaryLinkColor: '#1E4794',
  quaternaryBorderColor: '#E5E8EE',
  quaternaryBackgroundLight: '#FFFFFF',
  transparentBackground: 'rgba(27, 42, 78, 0.1)',
  mapPinForSale: '#1E4794',
  mapPinForSaleFocus: '#1B2A4E',
  mapControlBackground: '#FFFFFF',
  mapControlBackgroundHover: '#EDF0F6',
  mapControlBackgroundActive: '#E5E8EE',
  carouselArrowHover: '#EDF0F6',
  carouselArrowFocus: '#E5E8EE',
  notFound404PinHeadColor: '#1E4794',
  notFound404PinBodyColor: '#1B2A4E',
};

const AgntIncTheme: ThemeConfig<typeof AGNT_INC_THEME_NAME> = {
  id: AGNT_INC_THEME_NAME,
  name: 'AGNT Inc.',
  title: 'AGNT, Inc. — Redefining How the World Works Together',
  metaDescription: 'AGNT, Inc. (Nasdaq: AGNT) is a holding company for agent-centric real estate brands including eXp Realty, the world\'s largest independent real estate brokerage.',
  largeDesktopHeaderImage: undefined,
  desktopHeaderImage: undefined,
  mobileHeaderImage: undefined,
  smallMobileImage: undefined,
  ogSiteName: 'AGNT, Inc.',
  ogImage: `${AGNT_INC_WP_UPLOADS_URL}/2026/06/AGNT_OG.png`,
  ogImageAlt: 'AGNT, Inc. logo',
  schemaUrl: 'https://www.agnt.inc',
  schemaLogo: `${AGNT_INC_WP_UPLOADS_URL}/2026/06/AGNT_OG.png`,
  schemaLocation: {
    addressCountry: 'United States',
    addressLocality: 'Bellingham',
    addressRegion: 'WA',
    postalCode: '98226',
    streetAddress: '2219 Rimland Drive Suite 301',
  },
  schemaFoundingDate: '2008',
  schemaFounders: [
    {
      '@type': 'Person',
      name: 'Glenn Sanford',
    },
  ],
  faviconPath: '/favicon-agnt.png',
  colors: AgntIncThemeColors,
  fonts: {
    // Manrope is already self-hosted via eXp In Service — no new font load needed
    heading: 'Manrope',
    text: 'Manrope',
    headingWeight: '700',
    headingBoldWeight: '700',
  },
  decorations: {
    brushstrokes: false,
    boxShadow: '0px 2px 8px 0px rgba(27,42,78,0.12)',
    boxShadowLight: '0 0 8px 0 rgba(27,42,78,0.08)',
    areaPageLineWidth: '48px',
  },
  buttons: {
    borderRadius: '100px',
    borderRadiusSmall: '8px',
    borderRadiusMedium: '8px',
    borderRadiusLarge: '100px',
    borderRadiusModal: '12px',
    borderRadiusBanner: '12px',
  },
  icons: {
    borderRadius: '100px',
  },
  // searchOptions, contentBlockBuy/Sell required by the ThemeConfig interface but
  // not rendered on this marketing-only tenant (no listing pages).
  searchOptions: [],
  contentBlockBuy: {
    title: '',
    copy: '',
    image: '',
    link: '',
  },
  contentBlockSell: {
    title: '',
    copy: '',
    image: '',
    link: '',
  },
  privacyPolicy: (_language?: SiteLanguage) => '/privacy-policy',
  termsOfUse: (_language?: SiteLanguage) => '/terms-of-service',
};

export default AgntIncTheme;
